import { useForm, getFormProps } from '@conform-to/react'
import { getZodConstraint, parseWithZod } from '@conform-to/zod'
import { invariantResponse } from '@epic-web/invariant'
import * as ToggleGroupPrimitive from '@radix-ui/react-toggle-group'
import {
	json,
	type HeadersFunction,
	type LinksFunction,
	type MetaFunction,
	type LoaderFunctionArgs,
	type ActionFunctionArgs,
} from '@remix-run/node'
import {
	Form,
	Link,
	Links,
	Meta,
	Outlet,
	Scripts,
	ScrollRestoration,
	useFetcher,
	useLoaderData,
	useLocation,
	useMatches,
	useSubmit,
} from '@remix-run/react'
import { withSentry } from '@sentry/remix'
import { useRef } from 'react'
import { HoneypotProvider } from 'remix-utils/honeypot/react'
import { z } from 'zod'
import { Logo } from '#app/components/logo.tsx'
import { Navbar } from '#app/components/navbar.tsx'
import { ProgressBar } from '#app/components/progress-bar.tsx'
import {
	ContextMenu,
	ContextMenuContent,
	ContextMenuItem,
	ContextMenuTrigger,
} from '#app/components/ui/context-menu.tsx'
import { legalPopupCookie } from '#app/utils/legap-popup.server.ts'
import { Confetti } from './components/confetti.tsx'
import { GeneralErrorBoundary } from './components/error-boundary.tsx'
import { useToast } from './components/toaster.tsx'
import { Button } from './components/ui/button.tsx'
import {
	DropdownMenu,
	DropdownMenuContent,
	DropdownMenuItem,
	DropdownMenuPortal,
	DropdownMenuTrigger,
} from './components/ui/dropdown-menu.tsx'
import { Icon, href as iconsHref } from './components/ui/icon.tsx'
import { EpicToaster } from './components/ui/sonner.tsx'
import fontStyleStyleSheetUrl from './styles/font.css?url'
import tailwindStyleSheetUrl from './styles/tailwind.css?url'
import { logout, getUserId } from './utils/auth.server.ts'
import { ClientHintCheck, getHints, useHints } from './utils/client-hints.tsx'
import { getConfetti } from './utils/confetti.server.ts'
import { prisma } from './utils/db.server.ts'
import { getEnv } from './utils/env.server.ts'
import { honeypot } from './utils/honeypot.server.ts'
import {
	cn,
	combineHeaders,
	getDomainUrl,
	getUserImgSrc,
} from './utils/misc.tsx'
import { useNonce } from './utils/nonce-provider.ts'
import { useRequestInfo } from './utils/request-info.ts'
import { getTheme, setTheme, type Theme } from './utils/theme.server.ts'
import { makeTimings, time } from './utils/timing.server.ts'
import { getToast } from './utils/toast.server.ts'
import {
	useOptionalUser,
	useOptionalUserSettings,
	useUser,
} from './utils/user.ts'

export const links: LinksFunction = () => {
	return [
		// Preload svg sprite as a resource to avoid render blocking
		{ rel: 'preload', href: iconsHref, as: 'image' },
		// Preload CSS as a resource to avoid render blocking
		{ rel: 'preload', href: fontStyleStyleSheetUrl, as: 'style' },
		{ rel: 'preload', href: tailwindStyleSheetUrl, as: 'style' },
		{ rel: 'mask-icon', href: '/favicons/mask-icon.svg' },
		{
			rel: 'alternate icon',
			type: 'image/png',
			href: '/favicons/favicon-32x32.png',
		},
		{ rel: 'apple-touch-icon', href: '/favicons/apple-touch-icon.png' },
		{
			rel: 'manifest',
			href: '/site.webmanifest',
			crossOrigin: 'use-credentials',
		} as const, // necessary to make typescript happy
		//These should match the css preloads above to avoid css as render blocking resource
		{ rel: 'icon', type: 'image/svg+xml', href: '/favicons/favicon.svg' },
		{ rel: 'stylesheet', href: fontStyleStyleSheetUrl },
		{ rel: 'stylesheet', href: tailwindStyleSheetUrl },
	].filter(Boolean)
}

export const meta: MetaFunction<typeof loader> = ({ data }) => {
	const requestInfo = data?.requestInfo
	return [
		{ viewport: 'width=device-width,initial-scale=1,viewport-fit=cover' },
		{
			'theme-color':
				requestInfo?.userPrefs.theme === 'dark' ? '#020817' : '#FFF',
		},
		{ title: data ? 'X̂ Man' : 'Error | X̂ Man' },
		{ name: 'description', content: `Empower your financial freedom` },
	]
}

const LAST_UPDATED_LEGAL_COPY_DATE = new Date('2024-01-01').valueOf()

export async function loader({ request }: LoaderFunctionArgs) {
	const timings = makeTimings('root loader')
	const userId = await time(() => getUserId(request), {
		timings,
		type: 'getUserId',
		desc: 'getUserId in root',
	})

	const user = userId
		? await time(
				() =>
					prisma.user.findUniqueOrThrow({
						select: {
							id: true,
							name: true,
							username: true,
							image: { select: { id: true } },
							roles: {
								select: {
									name: true,
									permissions: {
										select: { entity: true, action: true, access: true },
									},
								},
							},
							settings: {
								select: {
									country: true,
									currency: true,
									locale: true,
									timeZone: true,
									homePage: true,
								},
							},
						},
						where: { id: userId },
					}),
				{ timings, type: 'find user', desc: 'find user in root' },
			)
		: null
	if (userId && !user) {
		console.info('something weird happened')
		// something weird happened... The user is authenticated but we can't find
		// them in the database. Maybe they were deleted? Let's log them out.
		await logout({ request, redirectTo: '/' })
	}
	const { toast, headers: toastHeaders } = await getToast(request)
	const { confettiId, headers: confettiHeaders } = getConfetti(request)
	const honeyProps = honeypot.getInputProps()
	const hints = getHints(request)

	const legalPopupCookieValue = await legalPopupCookie.parse(
		request.headers.get('cookie'),
	)
	const showLegalPopup = legalPopupCookieValue
		? legalPopupCookieValue < LAST_UPDATED_LEGAL_COPY_DATE
		: true

	return json(
		{
			user,
			requestInfo: {
				hints,
				origin: getDomainUrl(request),
				path: new URL(request.url).pathname,
				userPrefs: {
					theme: getTheme(request),
				},
			},
			ENV: {
				...getEnv(),
			},
			toast,
			confettiId,
			honeyProps,
			showLegalPopup,
		},
		{
			headers: combineHeaders(
				{ 'Server-Timing': timings.toString() },
				toastHeaders,
				confettiHeaders,
			),
		},
	)
}

export const headers: HeadersFunction = ({ loaderHeaders }) => {
	const headers = {
		'Server-Timing': loaderHeaders.get('Server-Timing') ?? '',
	}
	return headers
}

const ThemeFormSchema = z.object({
	theme: z.enum(['system', 'light', 'dark']),
})

export async function action({ request }: ActionFunctionArgs) {
	const formData = await request.formData()
	const submission = parseWithZod(formData, {
		schema: ThemeFormSchema,
	})
	invariantResponse(submission.status === 'success', 'Invalid theme received')
	const { theme } = submission.value

	const responseInit = {
		headers: { 'set-cookie': setTheme(theme) },
	}
	return json({ result: submission.reply() }, responseInit)
}

function Document({
	children,
	nonce,
	theme,
	env = {},
}: {
	children: React.ReactNode
	nonce: string
	theme?: Theme
	env?: Record<string, string>
}) {
	const { theme: hintTheme } = useHints()
	return (
		<html
			lang="en"
			dir="ltr"
			className={`${theme || hintTheme || 'light'} h-full overflow-x-hidden`}
		>
			<head>
				<ClientHintCheck nonce={nonce} />
				<Meta />
				<meta charSet="utf-8" />
				<meta
					name="viewport"
					content="width=device-width,initial-scale=1,maximum-scale=1"
				/>
				<Links />
			</head>
			<body
				className="bg-background text-foreground"
				style={{
					overflow: 'initial !important',
				}}
			>
				{children}
				<script
					nonce={nonce}
					dangerouslySetInnerHTML={{
						__html: `window.ENV = ${JSON.stringify(env)}`,
					}}
				/>
				<ScrollRestoration nonce={nonce} />
				<Scripts nonce={nonce} />
			</body>
		</html>
	)
}

function App() {
	const data = useLoaderData<typeof loader>()
	const nonce = useNonce()
	const theme = useTheme()
	const matches = useMatches()
	const showFooter = matches.find(m => {
		if (m.id.match(/routes\/_marketing+/) || m.id.match(/routes\/_auth+/)) {
			return true
		}
		return false
	})
	useToast(data.toast)

	return (
		<Document nonce={nonce} theme={theme} env={data.ENV}>
			<div className="min-h-safe-screen flex flex-col justify-between">
				<Header />

				<div className="flex-1">
					<Outlet />
				</div>

				{showFooter ? <Footer /> : null}
			</div>
			<Confetti id={data.confettiId} />
			<EpicToaster closeButton position="top-center" theme={theme} />
			<ProgressBar />
			<LegalPopup />
		</Document>
	)
}

function AppWithProviders() {
	const data = useLoaderData<typeof loader>()
	return (
		<HoneypotProvider {...data.honeyProps}>
			<App />
		</HoneypotProvider>
	)
}

export default withSentry(AppWithProviders)

function Header() {
	const user = useOptionalUser()
	const location = useLocation()
	const matches = useMatches()
	const showNavBar = matches.find(m => {
		if (m.id.match(/routes\/_marketing+/) || m.id.match(/routes\/_auth+/)) {
			return true
		}
		return false
	})
	const homeLink = user ? '/home' : '/'
	const showHomeLink = location.pathname !== homeLink

	return (
		<header className="py-3 sm:py-6">
			<nav>
				<div className="container flex items-center justify-between gap-3">
					<ContextMenu>
						<ContextMenuTrigger asChild>
							<Link to="/" className={showNavBar ? '' : 'max-sm:mx-auto'}>
								<Logo type="horizontal-color" className="hidden sm:block" />
								<Logo type="x-color" className="sm:hidden" />
							</Link>
						</ContextMenuTrigger>
						<ContextMenuContent className="w-64">
							<ContextMenuItem asChild className="px-5 py-3">
								<Link to="/brand-assets" className="cursor-pointer">
									Brand Assets
								</Link>
							</ContextMenuItem>
							<ContextMenuItem asChild className="px-5 py-3">
								<Link
									to={location.pathname}
									target="_blank"
									className="cursor-pointer"
								>
									Open in a new tab
								</Link>
							</ContextMenuItem>
							{location.pathname !== '/app/dashboard' && user ? (
								<ContextMenuItem asChild className="px-5 py-3">
									<Link to="/app/dashboard" className="cursor-pointer">
										Dashboard
									</Link>
								</ContextMenuItem>
							) : null}
							{showHomeLink && (
								<ContextMenuItem asChild className="px-5 py-3">
									<Link to={homeLink} className="cursor-pointer">
										Home Page
									</Link>
								</ContextMenuItem>
							)}
						</ContextMenuContent>
					</ContextMenu>
					{showNavBar ? <Navbar /> : null}
					<div
						className={cn(
							'items-center gap-10',
							showNavBar ? 'hidden md:flex' : 'flex',
						)}
					>
						{user ? (
							<UserDropdown />
						) : (
							<div className="flex gap-4">
								<Button asChild variant="ghost" size="sm">
									<Link to="/login">Log In</Link>
								</Button>
								<Button asChild variant="accent" size="sm">
									<Link to="/signup">Sign up</Link>
								</Button>
							</div>
						)}
					</div>
				</div>
			</nav>
		</header>
	)
}

function Footer() {
	const { locale } = useOptionalUserSettings()
	const footerCopyrightYear = new Date().toLocaleDateString(locale, {
		year: 'numeric',
	})
	return (
		<footer className="border-t pb-8">
			<div className="container flex flex-row flex-wrap justify-between gap-10 pb-8 pt-10">
				<Link to="/" className="text-5xl">
					X̂ Man
				</Link>
				<div className="flex gap-16">
					<div>
						<p className="text-xl">
							<strong>Company</strong>
						</p>
						<ul className="flex flex-col gap-2 pt-3">
							<li>
								<Link to="/about" reloadDocument>
									About
								</Link>
							</li>
							<li>
								<Link to="/#how-it-works" reloadDocument>
									How it works
								</Link>
							</li>
							<li>
								<Link to="/pricing" reloadDocument>
									Pricing
								</Link>
							</li>
							<li>
								<Link to="/support" reloadDocument>
									Support
								</Link>
							</li>
							<li>
								<Link to="/sitemap.xml" reloadDocument>
									Sitemap
								</Link>
							</li>
						</ul>
					</div>
					<div>
						<p className="text-xl">
							<strong>Legal</strong>
						</p>
						<ul className="flex flex-col gap-2 pt-3">
							<li>
								<Link to="/privacy" reloadDocument>
									Privacy policy
								</Link>
							</li>
							<li>
								<Link to="/terms" reloadDocument>
									Terms of use
								</Link>
							</li>
						</ul>
					</div>
				</div>
			</div>
			<div className="border-t pt-4">
				<div className="container">
					<p className="text-right text-xs">
						© 2023-{footerCopyrightYear} Arpit Dalal. All rights reserved.
					</p>
				</div>
			</div>
		</footer>
	)
}

function UserDropdown() {
	const user = useUser()
	const submit = useSubmit()
	const formRef = useRef<HTMLFormElement>(null)

	return (
		<DropdownMenu>
			<DropdownMenuTrigger asChild>
				<Button
					asChild
					variant="secondary"
					className="h-max rounded-full p-0 sm:h-10 sm:rounded-md sm:px-4 sm:py-2"
				>
					<Link
						prefetch="intent"
						className="flex items-center gap-2 max-sm:hidden"
						to={`/app/me`}
						// Progressive enhancement: we don't want to navigate to the
						// user's profile page when they click on their avatar because
						// the dropdown menu will open. This will prevent the navigation
						// from asChild{event => event.preventDefault()}
					>
						<img
							className="h-8 w-8 rounded-full object-cover"
							alt={user.name ?? user.username}
							src={getUserImgSrc(user.image?.id)}
						/>
						<span className="max-w-[200px] truncate text-body-sm">
							<strong>{user.name ?? user.username}</strong>
						</span>
					</Link>
				</Button>
			</DropdownMenuTrigger>
			<DropdownMenuPortal>
				<DropdownMenuContent sideOffset={8} align="start">
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/app/me`}>
							<Icon className="text-body-md" name="user">
								Profile
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/app/dashboard`}>
							<Icon className="text-body-md" name="layout-dashboard">
								Dashboard
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem asChild>
						<Link prefetch="intent" to={`/settings/profile`}>
							<Icon className="text-body-md" name="settings">
								Settings
							</Icon>
						</Link>
					</DropdownMenuItem>
					<DropdownMenuItem
						asChild
						// this prevents the menu from closing before the form submission is completed
						onSelect={event => {
							event.preventDefault()
							submit(formRef.current)
						}}
					>
						<Form action="/logout" method="POST" ref={formRef}>
							<Icon className="text-body-md" name="logout">
								<button type="submit">Logout</button>
							</Icon>
						</Form>
					</DropdownMenuItem>
				</DropdownMenuContent>
			</DropdownMenuPortal>
		</DropdownMenu>
	)
}

/**
 * @returns the user's theme preference, or the client hint theme if the user
 * has not set a preference.
 */
export function useTheme() {
	const hints = useHints()
	const requestInfo = useRequestInfo()
	const optimisticMode = useOptimisticThemeMode()
	if (optimisticMode) {
		return optimisticMode === 'system' ? hints.theme : optimisticMode
	}
	return requestInfo.userPrefs.theme ?? hints.theme
}

/**
 * If the user's changing their theme mode preference, this will return the
 * value it's being changed to.
 */
export function useOptimisticThemeMode() {
	const themeSwitchFetcher = useFetcher({ key: 'theme-switch' })

	if (themeSwitchFetcher && themeSwitchFetcher.formData) {
		const submission = parseWithZod(themeSwitchFetcher.formData, {
			schema: ThemeFormSchema,
		})

		if (submission.status === 'success') {
			return submission.value.theme
		}
	}
}

const toggleGroupClassName =
	'data-[state=on]:bg-accent data-[state=on]:border-secondary data-[state=on]:border flex items-center justify-center text-foreground px-3 py-1 first:rounded-l last:rounded-r ring-offset-background focus:z-10 outline-none focus:outline-none focus:ring-2 focus:ring-ring focus:ring-offset-2 transition-colors'
export function ThemeSwitch({
	userPreference,
}: {
	userPreference?: Theme | null
}) {
	const optimisticMode = useOptimisticThemeMode()
	const mode = optimisticMode ?? userPreference ?? 'system'

	const fetcher = useFetcher<typeof action>({ key: 'theme-switch' })

	const [form, fields] = useForm({
		id: 'theme-switch',
		constraint: getZodConstraint(ThemeFormSchema),
		lastResult: fetcher.data?.result,
		onValidate({ formData }) {
			return parseWithZod(formData, { schema: ThemeFormSchema })
		},
	})

	return (
		<fetcher.Form
			method="POST"
			action="/"
			className="flex w-full flex-wrap items-center justify-between gap-3"
			{...getFormProps(form)}
		>
			<p>Appearance</p>
			<ToggleGroupPrimitive.Root
				type="single"
				className="flex divide-x rounded-md border"
				value={mode}
				defaultValue={fields.theme.initialValue}
				onValueChange={value => {
					fetcher.submit(
						{
							theme: value,
						},
						{
							method: 'POST',
							action: '/',
						},
					)
				}}
			>
				<ToggleGroupPrimitive.Item
					className={toggleGroupClassName}
					value="system"
					aria-label="System"
				>
					<Icon name="device-laptop" />
				</ToggleGroupPrimitive.Item>
				<ToggleGroupPrimitive.Item
					className={toggleGroupClassName}
					value="dark"
					aria-label="Dark"
				>
					<Icon name="moon-stars" />
				</ToggleGroupPrimitive.Item>
				<ToggleGroupPrimitive.Item
					className={toggleGroupClassName}
					value="light"
					aria-label="Light"
				>
					<Icon name="sun-high" />
				</ToggleGroupPrimitive.Item>
			</ToggleGroupPrimitive.Root>
		</fetcher.Form>
	)
}

function LegalPopup() {
	let { showLegalPopup, ...loaderData } = useLoaderData<typeof loader>()
	const legalPopupFetcher = useFetcher({ key: 'legal-popup' })
	const user = useOptionalUser()
	const { pathname, search } = useLocation()
	const matches = useMatches()
	const isMarketingOrAuthPage = matches.find(m => {
		if (m.id.match(/routes\/_marketing+/) || m.id.match(/routes\/_auth+/)) {
			return true
		}
		return false
	})

	if (
		!showLegalPopup ||
		legalPopupFetcher.state !== 'idle' ||
		loaderData.ENV.MODE === 'development' ||
		loaderData.ENV.CI ||
		!isMarketingOrAuthPage ||
		user
	) {
		return null
	}

	return (
		<div className="fixed bottom-4 left-4 right-4 z-20 rounded-lg border-1 bg-muted p-4 max-xs:pt-10 md:right-auto">
			<p>We only use functional cookies.</p>
			<p>
				By using this website, you accept our{' '}
				<Link to="/terms" className="underline">
					Terms of Use
				</Link>{' '}
				and{' '}
				<Link to="/privacy" className="underline">
					Privacy Policy
				</Link>
			</p>
			<legalPopupFetcher.Form method="POST" action="/resources/legal-popup">
				<input type="hidden" name="redirectTo" value={`${pathname}${search}`} />
				<Button variant="ghost" size="icon" className="absolute right-0 top-0">
					<Icon name="x" />
				</Button>
			</legalPopupFetcher.Form>
		</div>
	)
}

export function ErrorBoundary() {
	// the nonce doesn't rely on the loader so we can access that
	const nonce = useNonce()

	// NOTE: you cannot use useLoaderData in an ErrorBoundary because the loader
	// likely failed to run so we have to do the best we can.
	// We could probably do better than this (it's possible the loader did run).
	// This would require a change in Remix.

	// Just make sure your root route never errors out and you'll always be able
	// to give the user a better UX.

	return (
		<Document nonce={nonce}>
			<GeneralErrorBoundary />
		</Document>
	)
}
